// @flow
import React, { Component } from 'react';
import styled, { withTheme } from 'styled-components';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { translate } from 'react-i18next';
import { Dialog } from 'common/components/ui/Dialog';
import Button from 'common/components/ui/Button';
import TextBox from 'common/components/ui/TextBox';
import DevFlag, { isDevFlagEnabled } from 'common/components/DevFlag';
import { openSnackbar } from 'common/modules/ui/actions/uiActions';
import LoadingSpinner from 'common/components/LoadingSpinner';
import Email from 'statsradio/domain/Email';
import { inviteCustomer } from '../actions/customersActions';
import type { CustomerInvitation } from '../types/RadioCustomer';
import RadioCustomer from '../types/RadioCustomer';
import GiveAccessToFolloCheckBox from './GiveAccessToFolloCheckBox';

const Wrapper = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  padding-bottom: ${({ theme }) => theme.metrics.largeMargin}px;
  padding-left: ${({ theme }) => theme.metrics.smallMargin}px;
  padding-right: ${({ theme }) => theme.metrics.smallMargin}px;
`;

const Row = styled.div`
  justify-content: center;
  padding-top: ${({ theme }) => theme.metrics.largeMargin}px;
`;

type Props = {
  onClose: () => void,
  onAddCustomer: (customer: RadioCustomer) => void,
  visible: boolean,
  inviting: boolean,
  invitationError: boolean,
  t: any,
  theme: any,
  actions: {
    inviteCustomer: (customer: CustomerInvitation) => Promise<*>,
    openSnackbar: (type: string, message: string) => mixed,
  },
}

type State = {
  advertiser: string;
  advertiserContact: { name: string, surname: string, email: string };
  hasAttemptedToSubmit: boolean;
  giveAccessToFollo: boolean;
};

const DEFAULT_STATE: State = {
  advertiser: '',
  advertiserContact: { name: '', surname: '', email: '' },
  hasAttemptedToSubmit: false,
  giveAccessToFollo: !isDevFlagEnabled('FOLLO_WITHOUT_FOLLO'),
};

class AddCustomerDialog extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = { ...DEFAULT_STATE };
  }

  onClose = () => {
    this.clearForm();
    this.props.onClose();
  }

  onAddCustomerSuccess = (customer: RadioCustomer) => {
    this.clearForm();
    this.props.onAddCustomer(customer);
  };

  onNameChanged = (text: string) => {
    const advertiserContact = { ...this.state.advertiserContact, name: text };
    this.setState({ advertiserContact });
  }

  onEmailChanged = (text: string) => {
    const advertiserContact = { ...this.state.advertiserContact, email: text };
    this.setState({ advertiserContact });
  }

  onAdvertiserChanged = (text: string) => {
    this.setState({ advertiser: text });
  }

  onGiveAccessToFolloChanged = (checked: boolean) => {
    this.setState({ giveAccessToFollo: checked });
  }

  onAddCustomer = (): void => {
    const { t } = this.props;
    this.setState({ hasAttemptedToSubmit: true });

    const { advertiser, advertiserContact: { name, email }, giveAccessToFollo } = this.state;
    if (advertiser && name && email && Email.isValid(email)) {
      const invitation = {
        advertiserName: advertiser,
        emailAddress: email,
        fullname: name,
        giveAccessToFollo,
      };

      this.props.actions.inviteCustomer(invitation).then((invitedCustomer) => {
        if (this.props.invitationError) {
          this.props.actions.openSnackbar('error', t('couldNotAddCustomerMessage'));
        } else {
          this.props.actions.openSnackbar('message', t('createdCustomerSuccess'));
          this.onAddCustomerSuccess(invitedCustomer);
        }
      });
    }
  }

  clearForm = () => {
    this.setState({ ...DEFAULT_STATE });
  }

  getEmailErrorMessage = (): string => {
    const { t } = this.props;
    const { hasAttemptedToSubmit, advertiserContact: { email } } = this.state;

    if (hasAttemptedToSubmit) {
      if (!email) {
        return t('missingEmailMessage');
      }

      if (email && !Email.isValid(email)) {
        return t('invalidEmailMessage');
      }
    }

    return '';
  }

  render() {
    const { t, theme } = this.props;
    const { hasAttemptedToSubmit, advertiser, advertiserContact: { name, email }, giveAccessToFollo } = this.state;

    const emailErrorMessage: string = this.getEmailErrorMessage();

    const actions = [
      <Button key="cancel" onClick={this.onClose} variant="outlined" color="primary">{t('close')}</Button>,
      <Button key="add" onClick={this.onAddCustomer} variant="contained" color="primary">{t('add')}</Button>,
    ];

    return (
      <Dialog
        title={t('createACustomer')}
        open={this.props.visible}
        disableScroll
        actions={actions}
        onClose={this.onClose}
        displayActions={!this.props.inviting}
      >
        {!this.props.inviting &&
        <React.Fragment>
          <Row className="row">
            <div className="col-lg-11 col-xs-11">
              <TextBox
                label={t('company')}
                inError={hasAttemptedToSubmit && !advertiser}
                errorText={t('missingAdvertiserMessage')}
                value={advertiser}
                onChange={this.onAdvertiserChanged}
                name="advertiser"
              />
            </div>
          </Row>
          <Row className="row">
            <div className="col-lg-11 col-xs-11">
              <TextBox
                label={t('email')}
                inError={emailErrorMessage !== ''}
                errorText={emailErrorMessage}
                value={email}
                onChange={this.onEmailChanged}
                name="email"
              />
            </div>
          </Row>
          <Row className="row">
            <div className="col-lg-11 col-xs-11">
              <TextBox
                label={t('fullname')}
                inError={hasAttemptedToSubmit && !name}
                errorText={t('missingFullnameMessage')}
                value={name}
                onChange={this.onNameChanged}
                name="name"
              />
            </div>
          </Row>
          <DevFlag flag="FOLLO_WITHOUT_FOLLO">
            <Row className="row">
              <div className="col-lg-11 col-xs-11">
                <GiveAccessToFolloCheckBox
                  onCreation
                  giveAccessToFollo={giveAccessToFollo}
                  onGiveAccessToFolloChanged={this.onGiveAccessToFolloChanged}
                />
              </div>
            </Row>
          </DevFlag>
        </React.Fragment>
        }
        {this.props.inviting && <Wrapper style={{ paddingTop: theme.metrics.largeMargin }}><LoadingSpinner size="medium" /></Wrapper>}
      </Dialog>
    );
  }
}

function mapStateToProps(state): Object {
  return {
    inviting: state.customers.customerInvitation.sending,
    invitationError: state.customers.customerInvitation.error,
  };
}

function mapDispatchToProps(dispatch: *): Object {
  return {
    actions: bindActionCreators({ inviteCustomer, openSnackbar }, dispatch),
  };
}

export default translate('customers')(withTheme(connect(mapStateToProps, mapDispatchToProps)(AddCustomerDialog)));



// WEBPACK FOOTER //
// ./src/statsradio/modules/customers/components/AddCustomerDialog.js